import React, { useEffect, useMemo } from 'react';
import { Input } from 'formik-antd';
import { Row, Col, Divider } from 'antd';
import { FormikProps } from 'formik';
import * as Yup from 'yup';

import {
  statesSelector,
  citiesSelector,
  isStatesLoadingSelector,
  isCitiesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import { BankingInfoFormValues } from '../EditBankingInfoModal';
import { BOUsersBankingInfoTypeEnum } from 'api/backofficeApiClient';
import { maxTextInputCharactersLength } from 'utils/validationUtils';
import { getAllStates, getAllCities } from 'redux/reducers/regionSlice';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';

export interface MailTheChecksFormValues {
  payableTo: string;
  checkState?: number;
  checkCity: string;
  checkAddress: string;
  checkZip: string;
  mailCheckConfirmation: boolean;
}

export const isMailTheChecksTypeSelected = (
  bankingInfoType: BOUsersBankingInfoTypeEnum,
) => bankingInfoType === BOUsersBankingInfoTypeEnum.MailMyChecks;

export const mailTheChecksValidationSchema = Yup.object({
  payableTo: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `Payable to should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  checkState: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `State should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  checkCity: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `City should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  checkAddress: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxTextInputCharactersLength,
      `Address should be ${maxTextInputCharactersLength} characters maximum`,
    ),
  checkZip: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, 'ZIP code should be 120 characters maximum'),
  mailCheckConfirmation: Yup.boolean().oneOf(
    [true],
    'Please authorize myBasePay, LLC distribute ‘member commissions’ in the form of a live check',
  ),
});

type Props = {
  formik: FormikProps<BankingInfoFormValues>;
};

export default function MailMyChecks(props: Props) {
  const { formik } = props;
  const dispatch = useAppDispatch();
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === countryUSAid),
    [states],
  );

  const filteredCities = useMemo(
    () =>
      cities.filter(
        (city) =>
          city.stateId === formik?.values?.mailMyChecksDetail?.checkState,
      ),
    // eslint-disable-next-line
    [formik?.values?.mailMyChecksDetail?.checkState, cities],
  );

  const handleStateChange = () => {
    formik?.setFieldValue('mailMyChecksDetail.checkCity', '', false);
  };

  useEffect(() => {
    dispatch(getAllStates());
    dispatch(getAllCities());
  }, [dispatch]);

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <FormItem name="mailMyChecksDetail.payableTo" label="Payable to *">
            <Input
              name="mailMyChecksDetail.payableTo"
              placeholder="Enter payable to information"
              autoComplete="off"
            />
          </FormItem>
        </Col>
      </Row>

      <Divider className="modal-divider after-form" />

      <Row gutter={40}>
        <Col xs={12}>
          <div className="modal-title">Check address</div>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <FormItem name="mailMyChecksDetail.checkState" label="State *">
                <FormikSelectWithLoading
                  isLoading={isStatesLoading}
                  name="mailMyChecksDetail.checkState"
                  showSearch
                  placeholder="Select state"
                  optionFilterProp="children"
                  onChange={handleStateChange}
                >
                  {filteredStates.map((state) => (
                    <FormikSelectWithLoading.Option
                      key={`select-state-${state.id}`}
                      value={state.id || -1}
                    >
                      {state.name}
                    </FormikSelectWithLoading.Option>
                  ))}
                </FormikSelectWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem name="mailMyChecksDetail.checkCity" label="City *">
                <FormikAutoCompleteWithLoading
                  isLoading={isCitiesLoading}
                  name="mailMyChecksDetail.checkCity"
                  filterOption
                  disabled={!formik.values.mailMyChecksDetail.checkState}
                  placeholder="Enter city"
                  showSearch
                >
                  {filteredCities.map((city) => (
                    <FormikAutoCompleteWithLoading.Option
                      key={`city-state-${city.id}`}
                      value={city.name || ''}
                    >
                      {city.name}
                    </FormikAutoCompleteWithLoading.Option>
                  ))}
                </FormikAutoCompleteWithLoading>
              </FormItem>
            </Col>
          </Row>
        </Col>

        <Col xs={24} lg={12}>
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <FormItem
                name="mailMyChecksDetail.checkAddress"
                label="Address *"
              >
                <Input
                  name="mailMyChecksDetail.checkAddress"
                  placeholder="Enter address"
                  autoComplete="off"
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem label="ZIP code *" name="mailMyChecksDetail.checkZip">
                <Input
                  name="mailMyChecksDetail.checkZip"
                  className="full-width"
                  placeholder="Enter ZIP code"
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col xs={24}>
          <FormItem
            name="mailMyChecksDetail.mailCheckConfirmation"
            className="checkbox-wrapper"
          >
            <Checkbox name="mailMyChecksDetail.mailCheckConfirmation">
              I hereby authorize myBasePay, LLC distribute ‘member commissions’
              in the form of a live check. I understand that myBasePay is not
              responsible for any shipping errors that may occur during the
              course of delivery. I also understand that myBasePay LLC will not
              be responsible for any lost or stolen checks that may result due
              to this form of delivery method. It is my responsibility to report
              to myBasePay LLC any lost or unreceived checks at which time
              myBasePay LLC will review the status of the check and reissue if
              necessary. I understand that it's my responsibility to make any
              changes to my mailing address information with myBasePay. I will
              not hold myBasePay liable for any delay in payment
            </Checkbox>
          </FormItem>
        </Col>
      </Row>
    </>
  );
}
